import { useCallback, useEffect, useRef } from 'react';
import { APPEAR_TRANSITION_DURATION } from '../Transitions/AppearTransition/constants';
import { useDebounce, useDeviceType, useDidUpdateEffect, useHoverEvents, useInputWithReady, useOutsideClick, useVariable } from '../hooks';
/*
 Helper which modifies initial callback and returns modified callbacks together with refs
 */

export var useTooltipEvents = function useTooltipEvents(_ref) {
  var _ref$defaultOpen = _ref.defaultOpen,
      defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
      openProp = _ref.open,
      _ref$trigger = _ref.trigger,
      triggerProp = _ref$trigger === void 0 ? 'hover' : _ref$trigger,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      onToggle = _ref.onToggle,
      onOpened = _ref.onOpened,
      onClosed = _ref.onClosed,
      onToggled = _ref.onToggled,
      onClick = _ref.onClick,
      onBeforeOutsideChange = _ref.onBeforeOutsideChange,
      onBeforeInsideChange = _ref.onBeforeInsideChange,
      _ref$closeOnOutsideCl = _ref.closeOnOutsideClick,
      closeOnOutsideClick = _ref$closeOnOutsideCl === void 0 ? true : _ref$closeOnOutsideCl,
      excludeRef = _ref.excludeRef,
      _ref$ready = _ref.ready,
      ready = _ref$ready === void 0 ? true : _ref$ready,
      onReady = _ref.onReady,
      closeDelay = _ref.closeDelay;

  var _useInputWithReady = useInputWithReady(openProp, defaultOpen, onToggle, undefined, undefined, undefined, onBeforeOutsideChange, onBeforeInsideChange, ready, onReady),
      opened = _useInputWithReady[0],
      setOpened = _useInputWithReady[1],
      openedRef = _useInputWithReady[2];

  var timeoutCloseId = useRef(null);

  var _useDeviceType = useDeviceType(),
      hasTouchScreen = _useDeviceType.hasTouchScreen;

  var trigger = triggerProp === null ? null : hasTouchScreen ? 'click' : triggerProp;
  var close = useCallback(function () {
    if (trigger === 'enter' && closeDelay > 0) {
      timeoutCloseId.current = setTimeout(function () {
        setOpened(false);
      }, closeDelay);
    } else {
      setOpened(false);
    }
  }, [trigger, closeDelay]);
  var open = useCallback(function () {
    if (trigger === 'enter' && timeoutCloseId.current) {
      clearTimeout(timeoutCloseId.current);
      timeoutCloseId.current = null;
    }

    setOpened(true);
  }, [trigger]);

  var _useOutsideClick = useOutsideClick(close, !closeOnOutsideClick || !opened, undefined, excludeRef),
      parentRef = _useOutsideClick.parentRef,
      childRef = _useOutsideClick.childRef;

  var openedDebounced = useDebounce(opened, APPEAR_TRANSITION_DURATION);
  var handleOpen = useVariable(onOpen);
  var handleClose = useVariable(onClose);
  var handleOpened = useVariable(onOpened);
  var handleClosed = useVariable(onClosed);
  var handleToggled = useVariable(onToggled);
  var handleClick = useVariable(onClick);
  var onMouseEnter = useCallback(function () {
    if (trigger === 'hover' || trigger === 'enter') {
      open();
    }
  }, [trigger, open]);
  var onMouseLeave = useCallback(function () {
    if (trigger === 'hover' || trigger === 'enter') {
      close();
    }
  }, [trigger, close]);

  var _useHoverEvents = useHoverEvents(onMouseEnter, onMouseLeave, parentRef, trigger === 'click' || trigger === 'enter' ? childRef : null, false, (closeDelay || 0) <= 0),
      onMouseOver = _useHoverEvents.onMouseOver,
      onMouseOut = _useHoverEvents.onMouseOut;

  var onMouseClick = useCallback(function (e) {
    handleClick.current == null ? void 0 : handleClick.current(e);

    if (trigger === 'click') {
      if (openedRef.current) {
        close();
      } else {
        open();
      }
    }
  }, [trigger, close, open]);
  var onChildMouseOver = useCallback(function () {
    if (trigger === 'enter' && timeoutCloseId.current) {
      clearTimeout(timeoutCloseId.current);
      timeoutCloseId.current = null;
    }
  }, [trigger]);
  useDidUpdateEffect(function () {
    if (opened) {
      handleOpen.current == null ? void 0 : handleOpen.current();
    } else {
      handleClose.current == null ? void 0 : handleClose.current();
    }
  }, [opened]);
  useDidUpdateEffect(function () {
    handleToggled.current == null ? void 0 : handleToggled.current(openedDebounced);

    if (openedDebounced) {
      handleOpened.current == null ? void 0 : handleOpened.current();
    } else {
      handleClosed.current == null ? void 0 : handleClosed.current();
    }
  }, [openedDebounced]);
  useEffect(function () {
    return function () {
      clearTimeout(timeoutCloseId.current);
    };
  }, []);
  return {
    parentRef: parentRef,
    childRef: childRef,
    opened: opened,
    openedDebounced: openedDebounced,
    close: close,
    open: open,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    onMouseClick: onMouseClick,
    onChildMouseOver: onChildMouseOver,
    onChildMouseOut: onMouseOut,
    setOpened: setOpened
  };
};